import React from 'react';

import {styled, Switch as MuiSwitch} from '@mui/material';
import { SwitchProps } from '@mui/material/Switch';

/**
 * VERA's own navy-to-purple ON fill, for the BASE theme only.
 *
 * Kept as a literal rather than derived from the ramp, for the reason
 * `CountBadge` records about the identical decision: Vera purple is not a
 * "fallback", it is the base theme's own accent. `apps/platform` is Vera's own
 * console and always renders this.
 */
const VERA_SWITCH_GRADIENT =
  'linear-gradient(122.76deg, #0B0A67 14.25%, #7850B9 84.95%)';

export const Switch = styled((props: SwitchProps) => (
  <MuiSwitch focusVisibleClassName=".Mui-focusVisible" disableRipple {...props} />
))(({ theme }) => {
  /**
   * WHITE-LABEL LEAK, fixed: the ON fill used to paint Vera's gradient on EVERY
   * tenant.
   *
   * The `backgroundColor: brand[600]` below was already a correct brand read —
   * in `apps/app` that ramp is derived from the tenant's own Studio
   * `primary_color` — but the `background-image` on the next line painted over
   * it unconditionally, so the read never reached a pixel and a customer's own
   * storefront showed Vera's purple on its Notifications, Share and gift
   * toggles. Same class of defect as a Vera-branded page background, arriving
   * through a shared component instead.
   *
   * Gated on `palette.brand.isTenantDerived` — the same signal `CountBadge` and
   * `LogoLoader` already branch on (`apps/app/src/settings/mui/brandRamp.ts`) —
   * so this is the split the codebase already makes everywhere else:
   *
   *   apps/platform       static Vera ramp, no such key -> gradient, as today
   *   apps/app, unthemed  `brandRamp(null)` -> false    -> gradient, as today
   *   apps/app, tenant    -> true -> flat `brand[600]`, the brand's own colour
   *
   * `apps/platform` therefore cannot move: nothing there ever sets the flag.
   *
   * NOTE the "5.24:1 in light" figure quoted in the OFF-state note below is the
   * GRADIENT's number and still describes the base theme. A tenant fill spans
   * the whole gamut, so its ON contrast is the brand's to own — exactly as it
   * already is for every other tenant-coloured fill (buttons, `CountBadge`).
   * What signals "a control is here" regardless is the OFF-state border, which
   * is untouched and is what was actually reported as invisible.
   */
  const brandPalette = theme.palette.brand as typeof theme.palette.brand & {
    isTenantDerived?: boolean;
  };
  const tenantDerived = Boolean(brandPalette?.isTenantDerived);

  return {
  width: 42,
  height: 22,
  padding: 0,
  '& .MuiSwitch-switchBase': {
    padding: 0,
    margin: 1,
    transitionDuration: '300ms',
    '&.Mui-checked': {
      transform: 'translateX(19.5px)',
      color: '#fff',
      '& + .MuiSwitch-track': {
        backgroundColor: theme.palette.brand[600],
        backgroundImage: tenantDerived ? 'none' : VERA_SWITCH_GRADIENT,
        opacity: 1,
        border: 0,
        ...theme.applyStyles('dark', {
          backgroundColor: theme.palette.brand[500],
          // Only the base theme re-states the gradient. On a tenant the `none`
          // above already holds, so dark falls through to `brand[500]` — the
          // brand's own lighter step, which is the point of the mode split.
          ...(tenantDerived ? {} : { backgroundImage: VERA_SWITCH_GRADIENT }),
        }),
      },
      '&.Mui-disabled + .MuiSwitch-track': {
        opacity: 0.5,
      },
    },
    '&.Mui-focusVisible .MuiSwitch-thumb': {
      color: '#33cf4d',
      border: '6px solid #fff',
    },
    '&.Mui-disabled .MuiSwitch-thumb': {
      color: theme.palette.grey[100],
      ...theme.applyStyles('dark', {
        color: theme.palette.grey[600],
      }),
    },
    '&.Mui-disabled + .MuiSwitch-track': {
      opacity: 0.7,
      ...theme.applyStyles('dark', {
        opacity: 0.3,
      }),
    },
  },
  '& .MuiSwitch-thumb': {
    boxSizing: 'border-box',
    width: 20,
    height: 20,
  },
  '& .MuiSwitch-track': {
    borderRadius: 22 / 2,
    /**
     * ══════════════════════════════════════════════════════════════════════
     * LIGHT OFF: the boundary is the FILL. There is no border. (2026-11-04, VRG-836)
     * ══════════════════════════════════════════════════════════════════════
     *
     * Spencer, on the `#8d8d8d` hairline this file spent VRG-491 tuning: it "looks
     * bad in light mode — it's okay on dark mode", and then, explicitly, that a dark
     * 1px edge is OUT of light mode at any ratio. So the identifying contrast moved
     * off the edge and onto the track itself.
     *
     * `#8d8d8d`, measured against `background.default` (`#f4f5f5`, re-confirmed live
     * on 2026-11-04 via `getComputedStyle(document.body)` — NOT `primary[50]`
     * `#f6f6f6`, which is a nav/field token and reads ~1% too generous) and against a
     * white card (`#ffffff`, which is what the Labs gallery, the Companies table rows
     * and every settings Card actually render behind a switch):
     *
     *   fill #888888   3.245 : 1  vs page   3.545 : 1  vs white card
     *   fill #8d8d8d   3.039 : 1  vs page   3.319 : 1  vs white card   <- IS
     *   fill #8e8e8e   2.999 : 1  vs page   3.276 : 1  vs white card   FAILS
     *
     * ⚠️ THE SAME WALL, NOT A NEW ONE. `#8d8d8d` was the floor when it was a 1px ring
     * and it is the floor now that it is a 42x22 area, because WCAG 1.4.11 asks 3:1
     * of the colour, not of the number of pixels wearing it. Moving the boundary
     * bought SOFTNESS, not headroom. `#8e8e8e` still fails by 0.001. Do not "tidy"
     * this onto a palette token: `primary[300]` (`#b0b0b0`) is 1.986:1, which is not
     * a rounding of this, it is a failure.
     *
     * WHY A FILL READS LIGHTER THAN A STROKE AT THE IDENTICAL RATIO. Two reasons, and
     * the measured ratio is blind to both. A thin stroke reads darker than its own
     * luminance (the eye integrates a 1px line against everything around it), and
     * this particular stroke sat between the page on one side and a near-white
     * `#F0F0F1` fill on the other, so it carried the maximum available local edge
     * contrast — 2.914:1 against its own interior — on top of its 3.039:1 against the
     * page. A fill has no interior edge to pop against. Same number, no hairline.
     *
     * The capsule's rendered size is unchanged: 42x22 measured before and after.
     * `box-sizing: border-box` (CssBaseline, inherited) made the border size-neutral,
     * so removing it is size-neutral too. The one treatment that DID change the
     * geometry was an OUTER `box-shadow: 0 0 0 1px` ring, which renders 44x24 — it is
     * drawn outside the box and `border-box` does not contain it. Rejected for that.
     *
     * THE THUMB IS THE REAL WINNER HERE, and it was the weakest link all along. The
     * white thumb against the OFF track measured **1.139:1** on `#F0F0F1`, separated
     * almost entirely by MUI's `shadows[1]` rather than by colour. Thumb POSITION is
     * what communicates on-vs-off, so that is state information at 1.139:1. On
     * `#8d8d8d` it measures **3.319:1**. The accessibility case for this change is
     * stronger than the aesthetic one that prompted it.
     *
     * ──────────────────────────────────────────────────────────────────────
     * WHAT WAS TRIED AND REJECTED, with numbers (2026-11-04, measured in-browser on a
     * live page via cloned real DOM, both grounds, light and dark). Recorded so the
     * next person does not re-run the search:
     *
     *   hairline 0.5px #8d8d8d   looks lighter ONLY because it is not #8d8d8d. At
     *                            dpr 1 a half-pixel border composites at 50% coverage
     *                            and renders ~#c0c1c1 = 1.652:1 vs page. At dpr 2 it
     *                            is one device pixel at full colour = 3.039:1. So
     *                            compliance would depend on the viewer's display.
     *                            Rejected: zero headroom is not a hardware lottery.
     *   inset shadow, 0 blur     inset 0 0 0 1px #8d8d8d is the same colour at the
     *                            same place: identical ratio, visually identical to
     *                            the border. No gain.
     *   inset shadow, blurred    softest of everything tried, but no pixel reaches
     *                            full colour, so nothing measures 3:1. Rejected.
     *   pale ring + dark arc     ring #d8d8dc (1.301:1 — invisible) carrying the
     *                            capsule SHAPE, with one compliant inset arc. Reads
     *                            lighter, but the shape is the thing that identifies
     *                            a switch, and it would be carried by 1.3:1 pixels.
     *                            The text-field-underline precedent does not transfer:
     *                            there the underline IS the control's shape.
     *   cool hue at same L       #898d9c is 3.026:1 and dE 8.6 from #8d8d8d, so it is
     *                            a real hue change — but luminance is fixed by the
     *                            ratio, so it is a lateral move, not a lighter one.
     *   edge #949494             3.033:1 on white but 2.777:1 on the page. Only
     *                            compliant if no switch ever sits on `#f4f5f5`, which
     *                            is exactly the per-surface fragility to avoid.
     *
     * ──────────────────────────────────────────────────────────────────────
     * ⚠️ TWO CLAIMS THIS FILE USED TO MAKE ARE WRONG, and both argued against the fill
     * that now ships. Overturned on evidence, not preference:
     *
     * 1. "A mid-grey capsule is easily mistaken for ON." It is not. Rendered as a
     *    sweep (#F0F0F1 → #E9E9EA → #DEDEE0 → #D2D2D5 → #C0C0C0 → #8d8d8d) with the
     *    ON state beside each, OFF and ON are unambiguous at every step, because ON
     *    is a saturated navy-to-purple gradient and OFF is neutral grey — they differ
     *    in HUE, not merely in how full they look. The decisive evidence is in this
     *    very file: DARK MODE HAS ALWAYS SHIPPED EXACTLY THIS ARRANGEMENT — a filled
     *    grey `#39393D` capsule for OFF against the same purple for ON — and dark is
     *    the half nobody complained about.
     *
     * 2. "A border clears 3:1 on every surface, where a fill would have to be chosen
     *    per surface." There is no such asymmetry. Both sit adjacent to the page, so
     *    both answer to the same darkest light surface (`#f4f5f5`, not white — a dark
     *    colour gains contrast on white, so white is the generous case and never the
     *    binding one). The edge never had the portability advantage it was credited
     *    with; `#8d8d8d` is the floor for either instrument.
     *
     * RETIRED: the old "THE TRAP" cap that pinned the fill at `#F0F0F1`. It existed
     * to stop the EDGE popping against its own interior as the fill lightened
     * (edge-vs-fill had to stay under the 2.922 that the pre-VRG-491 `#888888`/
     * `#E9E9EA` pair measured). With no edge in light there is no edge-vs-fill
     * quantity, so the cap has no referent. Kept in the history above because it is
     * still the correct reasoning IF an edge ever returns.
     *
     * Disabled OFF keeps `opacity: 0.7`, so the fill lightens and drops under 3:1.
     * That is deliberate and allowed — 1.4.11 exempts inactive controls.
     *
     * Pinned by `apps/platform/test/switch-contrast.guard.spec.ts`, which recomputes
     * these ratios from the literals in this file rather than trusting the comment.
     * It lives under apps/platform because `pnpm test:guards` runs api + platform
     * only — a guard in `packages/ui` would never execute, and working-conventions
     * §30 records an unexecuted guard as strictly worse than none.
     */
    backgroundColor: '#8d8d8d',
    /**
     * HISTORY — the border that used to carry the OFF boundary in light, kept because
     * it is why the literals above are what they are. Superseded 2026-11-04 (VRG-836);
     * DARK still uses this instrument, unchanged, below.
     *
     * Both track fills started as MUI's iOS-demo literals, and neither had
     * enough contrast to be found on a Vera page. Measured, off state, AS
     * ORIGINALLY AUTHORED (light is `#F0F0F1` now — see the 2026-08-31 note
     * below; the figure only got smaller, so the argument is unweakened):
     *
     *   light   #e9e9ea on the #f4f5f5 page   1.11:1   (now #F0F0F1, 1.04:1)
     *   dark    #39393d on the #09090b page   1.73:1   (unchanged)
     *
     * against the 3:1 WCAG 1.4.11 asks of a control's boundary. That is not
     * "a bit low" — a pale capsule on a pale page is a control the user cannot
     * see at all, which is what the Monthly/Annual toggle was reported as. The
     * ON state was never the problem (its brand gradient measures 5.24:1 in
     * light), so what actually failed was telling that a switch was THERE.
     *
     * A BORDER rather than a darker fill, for the reason the auth fields
     * settled on the same instrument: a 1px edge does not depend on luminance
     * headroom, so it clears 3:1 on every surface a switch sits on, where a
     * fill would have to be chosen per surface. Darkening the fill instead
     * would also cost the OFF state its "empty" reading: a mid-grey capsule is
     * easily mistaken for ON.
     *
     * ⚠️ BOTH SENTENCES ABOVE ARE FALSE and were overturned on 2026-11-04 — see
     * "TWO CLAIMS THIS FILE USED TO MAKE ARE WRONG" in the live block above. The
     * per-surface asymmetry does not exist (edge and fill answer to the same
     * `#f4f5f5`), and dark mode had already been shipping the "mistaken for ON"
     * arrangement, uncomplained-about, the entire time this paragraph stood.
     * Left in place because it is the reasoning the light border was chosen on.
     *
     * ⚠️ CORRECTION (2026-08-31, measured on a live page in both modes). This
     * paragraph used to claim ONE value — `primary[400]` — cleared 3:1
     * everywhere, and quoted "dark page 5.61:1, dark card 4.63:1". Those are
     * `#888888`'s numbers, and `#888888` has never been what dark renders.
     * `primary[400]` is itself mode-dependent: `#888888` in the light theme,
     * `#71717a` (zinc-500) in the dark one. So a single token reference was
     * always TWO rendered values, and the dark half measured:
     *
     *   #71717a   4.12:1 vs the #09090b page   3.96:1 vs a #0f0f12 card
     *
     * Lower than advertised, but still clear of 3:1 — the conclusion survived,
     * the arithmetic did not. Do not re-derive dark's contrast from `#888888`.
     *
     * Not `primary[100]`, the app's usual rest border: that token is calibrated
     * for a field on a WHITE card and measures 1.06:1 against this page. Same
     * trap `authFieldChrome` documents.
     *
     * Checked keeps `border: 0` above — the gradient carries its own contrast,
     * and `box-sizing: border-box` (CssBaseline, inherited) means adding or
     * removing this border does not change the capsule's rendered size.
     *
     * ──────────────────────────────────────────────────────────────────────
     * LIGHTENED 2026-08-31 (VRG-491). The light OFF state read too heavy, so
     * the edge went to the 3:1 floor and the fill came up with it. Both halves
     * are measured, and they are ONE decision — see the trap at the end.
     *
     * `#8d8d8d`, NOT the `primary[400]` token, and NOT `#8e8e8e`. Measured
     * against `background.default` (`#f4f5f5`, confirmed on a live page — NOT
     * `primary[50]` `#f6f6f6`, which is a nav/field token and gives answers ~1%
     * too generous):
     *
     *   #888888   3.245 : 1  vs page   3.545 : 1  vs white card   (was)
     *   #8d8d8d   3.039 : 1  vs page   3.319 : 1  vs white card   (is)
     *   #8e8e8e   2.999 : 1  vs page   3.276 : 1  vs white card   FAILS
     *
     * So `#8d8d8d` is the whole available headroom — one step further is below
     * WCAG 1.4.11's 3:1 for a component boundary. Do not "tidy" this literal
     * onto the nearest palette token: it is a measured floor, and `primary[300]`
     * (`#b0b0b0`, 1.99:1) is not a rounding of it, it is a failure.
     *
     * ⚠️ THE TRAP, and why the fill is capped at `#F0F0F1`. Lightening the fill
     * RAISES the edge's contrast against its own interior, so "lighter fill" and
     * "lighter edge" pull in opposite directions and a naive lightening makes the
     * control read HEAVIER. Edge-vs-fill, holding the edge at `#8d8d8d`:
     *
     *   fill #E9E9EA  2.736      fill #F0F0F1  2.914   <- cap, still under
     *   fill #EDEDEE  2.837      fill #F1F1F2  2.940   <- over baseline
     *   fill #EFEFF0  2.888      fill #F2F2F3  2.967   <- edge reads harder
     *
     * against the 2.922 the old `#888888`/`#E9E9EA` pair measured. `#F0F0F1` is
     * the LIGHTEST fill that still leaves the edge softer against its interior
     * than it was before — past it, you buy a lighter fill by making the ring
     * pop, which is the opposite of the ask. If you lighten the fill again,
     * re-run this number; it is the binding constraint, not the 3:1 floor.
     *
     * The white thumb is the other bound: `#F0F0F1` leaves it at 1.139:1 (from
     * 1.213:1), separated mostly by MUI's `shadows[1]` on the thumb rather than
     * by hue. There is not much left there either.
     *
     * DARK IS DELIBERATELY UNCHANGED, and the split below is how that is kept.
     * Lightening a border on a DARK ground ADDS contrast — the same edit reads
     * HEAVIER there, the opposite of the fix — and dark was never the
     * complaint. So light takes the literal and dark keeps resolving
     * `primary[400]`, which in the dark theme is `#71717a` and measures
     * 4.12:1 / 3.96:1 / 2.38:1 against the dark page / dark card / `#39393D`
     * fill. Verified in the browser after this change: byte-identical to what
     * dark rendered before it.
     *
     * This is LESS of a change than it looks. `primary[400]` already resolved
     * per mode (`#888888` light, `#71717a` dark), so the one-line token
     * reference was always two rendered values. All that is new is that the
     * LIGHT half is now an explicit measured literal instead of a token that
     * happened to sit near the floor. The fill has been mode-split all along.
     */
    border: 'none',
    opacity: 1,
    transition: theme.transitions.create(
      ['background-color', 'border-color'],
      {
        duration: 500,
      }
    ),
    ...theme.applyStyles('dark', {
      backgroundColor: '#39393D',
      /**
       * DARK IS UNTOUCHED — verified byte-identical in the browser before and after
       * VRG-836: fill `rgb(57, 57, 61)`, border `rgb(113, 113, 122)`, 1px solid, 42x22.
       *
       * It has to re-state the full `border` shorthand now rather than only
       * `borderColor`, because light sets `border: 'none'` — a lone `borderColor`
       * over a zero width paints nothing, which would have silently deleted dark's
       * boundary while every colour literal still looked right in source.
       *
       * `primary[400]` resolves per mode and this branch only ever runs in dark,
       * where it is `#71717a` (zinc-500): 4.117:1 vs the `#09090b` page, 3.960:1 vs a
       * `#0f0f12` card, 2.379:1 vs the `#39393D` fill. Do not re-derive dark's
       * contrast from `#888888` — that is the LIGHT resolution of the same token and
       * has never been what dark renders.
       *
       * Dark keeps the edge because dark never had the problem: its OFF fill is a
       * visible object in its own right (1.665:1 against the card), so the edge reads
       * as that object's rim. Light's old `#F0F0F1` fill was 1.043:1 against the
       * page — a sixteenth of the shape work — which left the hairline to be the
       * entire control. That asymmetry is the whole reason one half moved and the
       * other did not.
       */
      border: `1px solid ${(theme.palette as any).primary?.[400] || '#888888'}`,
    }),
  },
  };
});
